import services from '@/commons/services';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import converter from './monaco-converter';
import groovyBeautify from '@/commons/components/monaco-editor/basic-languages/groovy/beautify.js';

if(!window.monaco) {
    window.monaco = monaco;
}
const m2p = new converter.MonacoToProtocolConverter(monaco);
const p2m = new converter.ProtocolToMonacoConverter(monaco);

function createUri(model) {
    return model.uri.with({scheme: 'file', authority: null}).toString();
}

function createDocument(model) {
    return {uri: createUri(model), version: model.getVersionId(), languageId: model.getLanguageId(), text: model.getValue()}
}

function createRequest(model, position) {
    const r = {};
    if(model) {
        r.document = createDocument(model);
    }
    if(position) {
        const pos = m2p.asPosition(position.lineNumber, position.column);
        r.position = {line: pos.line, character: pos.character};
    }
    return r;
}

function createRequestFormat(model, options, range) {
    const r = createRequest(model);
    if(options) {
        const op = m2p.asFormattingOptions(options);
        r.options = op;
    }
    if(range) {
        const rng = m2p.asRange(range);
        r.range = {
            start: {line: rng.start.line, character: rng.start.character},
            end: {line: rng.end.line, character: rng.end.character}
        };
    }
    return r;
}

export default function (languageId, lang){
    const languageService = services.lsp;


    const disposables = [
        monaco.languages.registerCompletionItemProvider(languageId, {
            provideCompletionItems(model, position, context, token) {
                const wordUntil = model.getWordUntilPosition(position);
                const defaultRange = new monaco.Range(position.lineNumber, wordUntil.startColumn, position.lineNumber, wordUntil.endColumn);
                return languageService.doComplete(createRequest(model, position)).then(resp => {
                    const result = p2m.asCompletionResult(resp.data, defaultRange) || {}, labels = {};
                    result.suggestions = (result.suggestions || []).map(lang.mapSuggestion);
                    result.suggestions.forEach(s => labels[s.label] = true);
                    const snippets = lang.snippets(wordUntil.word, result.suggestions.length === 0);
                    snippets.forEach(s => {
                        if(!labels[s.label]) {
                            result.suggestions.push(s);
                        }
                    });
                    return result;
                });
            },

            resolveCompletionItem(item, token) {
                return item;
                // return jsonService.doResolve(m2p.asCompletionItem(item)).then(result => p2m.asCompletionItem(result, item.range));
            }
        }),

        monaco.languages.registerHoverProvider(languageId, {
            provideHover(model, position, token) {
                return languageService.doHover(createRequest(model, position)).then(resp => {
                    return p2m.asHover(resp.data);
                });
            }
        }),

        // Ctrl+Shift-I
        monaco.languages.registerDocumentFormattingEditProvider(languageId, {
            provideDocumentFormattingEdits(model, options, token) {
                const value = model.getValue();
                const text = groovyBeautify(value, { width: 120 }), range = model.getFullModelRange();
                return Promise.resolve([{ range, text }]);
                // return languageService.format(createRequestFormat(model, options)).then(resp => {
                //     return p2m.asTextEdits(resp.data);
                // });
            }
        }),

        // Ctrl+K Ctrl+F
        monaco.languages.registerDocumentRangeFormattingEditProvider(languageId, {
            provideDocumentRangeFormattingEdits(model, range, options, token) {
                const text = groovyBeautify(model.getValueInRange(range), {});
                return Promise.resolve([{
                    range: range,
                    text: text
                }]);
                // return languageService.format(createRequestFormat(model, options, range)).then(resp => {
                //     return p2m.asTextEdits(resp.data);
                // });
            }
        }),

        // Ctrl+Shift+O
        // https://code.visualstudio.com/docs/editor/editingevolved#_go-to-symbol
        // monaco.languages.registerDocumentSymbolProvider(languageId, {
        //     provideDocumentSymbols(model, token){
        //         return languageService.findDocumentSymbols(createRequest(model)).then(resp => {
        //             return p2m.asSymbolInformations(resp.data);
        //         })
        //     }
        // }),

        // validation
        (function (languageId) {
            const markerOwnerId = languageId + '.validation';
            const editor = monaco.editor;
            const disposables = [];
            const listeners = {};

            function setModelMarkers(model, markers){
                monaco.editor.setModelMarkers(model, markerOwnerId, markers || []);
            }

            function doValidate(model) {
                const req = createRequest(model);
                if(req.document.text.length === 0) {
                    setModelMarkers(model);
                    return;
                }
                languageService.doValidation(req).then(resp => {
                    const markers = p2m.asDiagnostics(resp.data);
                    setModelMarkers(model, markers);
                });
            }

            function onModelAdd(model) {
                if (model.getLanguageId() !== languageId) {
                    return;
                }
                let timeout;
                listeners[model.uri.toString()] = model.onDidChangeContent(function () {
                    clearTimeout(timeout);
                    timeout = setTimeout(function () { return doValidate(model); }, 1000);
                });
                doValidate(model);
            }

            function onModelRemoved(model) {
                if (model.getLanguageId() !== languageId) {
                    return;
                }
                setModelMarkers(model);
                const uriStr = model.uri.toString(), listener = listeners[uriStr];
                if (listener) {
                    listener.dispose();
                    delete listeners[uriStr];
                }
            }

            disposables.push(editor.onDidCreateModel(onModelAdd));
            disposables.push(editor.onWillDisposeModel(function (model) {
                onModelRemoved(model);
            }));
            disposables.push(editor.onDidChangeModelLanguage(function (event) {
                onModelRemoved(event.model);
                onModelAdd(event.model);
            }));
            editor.getModels().forEach(onModelAdd);

            return {
                dispose() {
                    editor.getModels().forEach(onModelRemoved);
                    disposables.forEach(d => d.dispose());
                }
            };
        })(languageId)
    ];


    return {
        dispose() {
            disposables.forEach(d => {
                try {
                    d.dispose();
                } catch (e) {
                    console.error(e);
                }
            });
        }
    }
};
